// src/components/negocios/TipoTabsBar.jsx
import React, { useMemo } from 'react';

/**
 * TipoTabsBar — Estilo Clean / Lovable
 * - Removemos a caixa/borda ao redor do componente.
 * - Agora é uma linha de abas contínua com border-bottom.
 */

function norm(s) {
  return String(s || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export default function TipoTabsBar({ filtros, onChange, counts = {} }) {
  const TABS = useMemo(
    () => [
      { key: 'all', label: 'Todos', setTipoImovel: '' },
      { key: 'casa', label: 'Casas', setTipoImovel: 'Casa' },
      { key: 'apto', label: 'Apartamentos', setTipoImovel: 'Apartamento' },
      {
        key: 'terreno',
        label: 'Terrenos / Áreas',
        setTipoImovel: 'Terreno / Área',
      },
      {
        key: 'comercial',
        label: 'Comercial',
        setTipoImovel: 'Ponto comercial',
      },
      {
        key: 'empreendimentos',
        label: 'Empreendimentos',
        setTipoImovel: 'Empreendimento',
      },
      { key: 'rural', label: 'Chácara / Fazenda', setTipoImovel: 'Chácara' },
    ],
    []
  );

  // Lógica para identificar a aba ativa baseada no filtro selecionado
  const activeKey = useMemo(() => {
    const t = norm(filtros?.tipoImovel);
    if (!t) return 'all';
    if (t === norm('casa')) return 'casa';
    if (t.includes('apartamento') || t.includes('kitnet')) return 'apto';
    if (t.includes('terreno') || t.includes('area')) return 'terreno';
    if (t.includes('comercial') || t.includes('galpao')) return 'comercial';
    if (t.includes('empreendimento')) return 'empreendimentos';
    if (t.includes('chacara') || t.includes('fazenda') || t.includes('sitio'))
      return 'rural';
    return 'all';
  }, [filtros?.tipoImovel]);

  const setTipo = (tipoImovel) => {
    onChange((prev) => ({
      ...prev,
      tipoImovel: tipoImovel || '',
    }));
  };

  return (
    // Alteração Visual: Removemos bordas, sombras e bg-white do container externo
    // Adicionamos border-b para criar a linha divisória que corre a tela
    <div className="w-full border-b border-gray-200 mt-4">
      <div className="flex items-end gap-6 overflow-x-auto px-2">
        {TABS.map((t) => {
          const active = activeKey === t.key;
          const c = Number(counts?.[t.key] ?? 0);

          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTipo(t.setTipoImovel)}
              className={[
                'group relative pb-3 text-sm whitespace-nowrap select-none transition-colors duration-200',
                // Fonte um pouco maior e mais legível
                active
                  ? 'text-blue-600 font-semibold'
                  : 'text-gray-500 hover:text-gray-800 font-medium',
              ].join(' ')}
            >
              <div className="flex items-center gap-1.5">
                <span>{t.label}</span>
                {/* Contador mais sutil */}
                <span
                  className={[
                    'text-xs transition-colors',
                    active
                      ? 'text-blue-600/80'
                      : 'text-gray-400 group-hover:text-gray-500',
                  ].join(' ')}
                >
                  ({c})
                </span>
              </div>

              {/* Linha azul inferior (Underline) - estilo Lovable */}
              {active && (
                <span className="absolute left-0 right-0 -bottom-[1px] h-[2px] bg-blue-600 rounded-t-full" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
